import { type ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { asyncStatus, NotificationsPopover } from "@playbookai/ui";
import { NOTIFICATIONS_ROUTE } from "../routes.js";
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationsQuery,
  useUnreadCountQuery,
} from "../notifications/useNotifications.js";
import {
  useOpenNotification,
  type OpenModuleEntityTab,
} from "../notifications/useOpenNotification.js";

/**
 * Host wiring for the TopBar's `notificationsSlot`. Reads the feed from the
 * host-owned notification feed client via TanStack Query and writes mark-read
 * through the shared mutations — the same query the `/notifications` page reads,
 * so the bell badge and the full page stay in sync. The data-blind contract
 * (`NotificationItem[]` in, callbacks out) is the deliverable; this component +
 * `notifications-client.ts` are the single swap point when `notification-client-ts`
 * ships (the swap is an import rename; these hooks don't change).
 *
 * `openModuleEntityTab` arrives as a PROP rather than from `useShellBridge`:
 * this renders in the TopBar, which sits outside the `ShellBridgeProvider` that
 * wraps the routed outlet. `AppLayout` owns the function and passes it down.
 */
export function NotificationsHost({
  openModuleEntityTab,
  resolveHref,
}: {
  readonly openModuleEntityTab?: OpenModuleEntityTab;
  readonly resolveHref?: (href: string, eventType?: string) => string;
}): ReactElement {
  const navigate = useNavigate();
  const feed = useNotificationsQuery();
  const items = feed.data ?? [];
  const { data: unreadCount } = useUnreadCountQuery();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const openNotification = useOpenNotification(openModuleEntityTab);

  return (
    <NotificationsPopover
      items={items.map((item) =>
        item.href && resolveHref
          ? { ...item, href: resolveHref(item.href, item.eventType) }
          : item,
      )}
      // `isPending`, not `isLoading`: a read not yet enabled during auth
      // hydration has fetched nothing and must read as loading, not as a
      // settled empty feed. A failure keeps rows it already showed (PB-5337).
      feedStatus={asyncStatus({
        isLoading: feed.isPending,
        isError: feed.isError,
        isEmpty: feed.isSuccess && items.length === 0,
        hasData: items.length > 0,
      })}
      onRetry={() => void feed.refetch()}
      // Fall back to the page-derived count while the total is loading, or when
      // its route is unavailable — it answers 503 when module enablement cannot
      // be resolved. Showing an under-count beats hiding the badge entirely
      // while unread rows are sitting in the feed. The library takes a number
      // and stays data-blind; choosing WHICH number is the host's call.
      unreadCount={unreadCount ?? items.filter((n) => n.unread).length}
      onMarkRead={markRead}
      onMarkAllRead={markAllRead}
      onViewAll={() => navigate(NOTIFICATIONS_ROUTE)}
      // PB-1583: a share/invite notification carries a REFERENCE to its
      // resource (`/app/solve/<id>`), not the tab-scoped route it actually
      // lives at. `useOpenNotification` resolves one into the other; anything
      // that is already a real route still navigates literally.
      // The clicked ROW, not a lookup by href: several notifications about one
      // workspace share an href, so `items.find(…)` could match a sibling and
      // label the opened tab with the wrong notification's title.
      onNavigate={(href, item) =>
        openNotification(
          items.find((original) => original.id === item.id)?.href ?? href,
          item.title,
          item.eventType,
        )
      }
    />
  );
}
